/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { createRef, useCallback, useEffect, useRef } from 'preact/compat';

import { useComponentTheme } from '#hooks/UNSAFE_useComponentTheme';
import { useFocus } from '#hooks/UNSAFE_useFocus';
import { useFormContext } from '#hooks/UNSAFE_useFormContext';
import { useFormFieldContext } from '#hooks/UNSAFE_useFormFieldContext';
import { useId } from '#hooks/UNSAFE_useId';
import { useTabbableMode } from '#hooks/UNSAFE_useTabbableMode';
import { useInteractionStyle } from '#hooks/UNSAFE_useInteractionStyle';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { useCheckboxRadioContext } from '#PRIVATE_CheckboxRadioField/CheckboxRadioContext';
import { CheckIcon } from '#PRIVATE_ThemedIcons/CheckIcon';
import { RadioOffIcon } from '#PRIVATE_ThemedIcons/RadioOffIcon';
import { RadioOnIcon } from '#PRIVATE_ThemedIcons/RadioOnIcon';
import { HiddenAccessible } from '#UNSAFE_HiddenAccessible';
import { useRadioSetContext } from '#UNSAFE_RadioSet/RadioSetContext';
import type { BundleType } from '#resources/nls/bundle';
import { IconUserAssistance } from '#UNSAFE_UserAssistance';
import { radioIconVars } from './themes/RadioIconContract.css';
import { RadioIconRedwoodTheme } from './themes/redwood/RadioIconTheme';
import { RadioRedwoodTheme } from './themes/redwood/RadioTheme';
import { useRadioItemContext } from './RadioItemContext';
import { getClientHints } from '#utils/PRIVATE_clientHints';
import type { RadioVariantOptions } from './themes/RadioStyles.css';

type Props = {
  /**
   * Text to provide guidance to help the user understand what data to enter.
   */
  assistiveText?: string;
  /**
   * Text that describes the radio button value.
   */
  children: string;
  /**
   * Help source URL associated with the component.
   */
  helpSourceLink?: string;
  /**
   * Custom text to be rendered for the `helpSourceLink`.
   */
  helpSourceText?: string;
  /**
   * Value of the radio button.
   */
  value: string | number;
};
/**
 * Returns the accessibility text for a radio button's selection state.
 *
 * The text is determined based on the client's platform (Mac or iOS/PC/Android) and the radio button's selection state.
 * Mac VoiceOver announces "selected" while others use "checked".
 *
 * @param {BundleType} translations The translation bundle containing the necessary text.
 * @param {boolean} isSelected Whether the radio button is currently selected.
 * @returns {string} The accessibility text for the radio button's selection state.
 */
export const getRadioSelectionText = (translations: BundleType, isSelected: boolean) => {
  const isMac = getClientHints().platform === 'mac';
  const translationKey = isMac
    ? isSelected
      ? 'radioButton_selected'
      : 'radioButton_unselected'
    : isSelected
    ? 'radioButton_checked'
    : 'radioButton_unchecked';

  return translations[translationKey]();
};

/**
 * RadioItem is an input element of type radio.
 */
export const RadioItem = ({ value, children, ...assistanceProps }: Props) => {
  const { isReadonly: isFormReadonly, isDisabled: isFormDisabled } = useFormContext();
  const { isDisabled: isFieldDisabled, isReadonly: isFieldReadonly } = useFormFieldContext();
  const { name, value: groupValue, onCommit, setFocusIndex } = useRadioSetContext();
  const { isFocusRingShown } = useCheckboxRadioContext();
  const { index } = useRadioItemContext();
  const { isTabbable } = useTabbableMode();
  const id = useId();
  const { isFocus, focusProps } = useFocus();
  const labelRef = createRef<HTMLLabelElement>();
  const isSelected = groupValue === value;
  const isDisabled = isFieldDisabled ?? isFormDisabled;
  const isReadonly = isFieldReadonly ?? isFormReadonly;
  const { interactionProps, applyActiveStyle } = useInteractionStyle();
  const inputRef = useRef<HTMLInputElement>(null);

  // JET-70100 - ensure radio button is scrolled into view when focused and focus ring is shown
  useEffect(() => {
    const isActiveElement = document.activeElement === inputRef?.current;
    if (!isActiveElement && isFocus && isFocusRingShown) {
      labelRef?.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [isFocus, isFocusRingShown, labelRef]);

  const { assistiveText, helpSourceLink } = assistanceProps;
  const hasUserAssistive = !isDisabled && !isReadonly && !!(assistiveText || helpSourceLink);
  const helpAvailableId = hasUserAssistive ? `${id}-help-available` : undefined;
  const uaId = hasUserAssistive ? `${id}-icon-ua` : undefined;
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const helpAvailableString = translations.formControl_helpAvailable();

  const IconComp = isReadonly
    ? isSelected
      ? CheckIcon
      : RadioOffIcon
    : isSelected
    ? RadioOnIcon
    : RadioOffIcon;

  const tabIndex = isTabbable !== false && ((index === 0 && !groupValue) || isSelected) ? 0 : -1;
  const accessibilityText = `${
    children || ''
  } ${translations.formControl_readOnly()} ${getRadioSelectionText(translations, isSelected)}`;

  const handleFocus = useCallback(() => {
    setFocusIndex?.(index);
  }, [setFocusIndex, index]);

  const handleLabelClick = useCallback(() => {
    inputRef.current?.focus();
    setFocusIndex?.(index);
  }, [inputRef, setFocusIndex, index]);

  const handleChange = useCallback(
    (e: Event) => {
      e.stopPropagation();
      onCommit?.({ previousValue: groupValue, value });
    },
    [onCommit, groupValue, value]
  );

  /**
   * JET-76970
   * Safari's `:focus-visible` is applied only during keyboard focus (e.g., Tab).
   * However, when a child of a card receives focus via arrow navigation, Safari
   * ignores `:focus-visible` even if it would match, and it only shows a focus
   * outline at navigation endpoints.
   *
   * For consistency:
   * - For Safari, add a class that forces the ring whenever focus is inside the
   *   card during keyboard navigation.
   * - For other browsers or readonly items, rely on native `:focus-visible` selectors.
   *
   * Known issue:
   * - Because of the timeout in useCollectionFocusRing, a long press on a item can
   *   sometimes trigger a focus ring, whereas a normal click does not.
   */
  const isSafari = getClientHints().browser === 'safari';
  const focusWithRing = isFocus && isFocusRingShown;
  const isFocusRingSelector = isReadonly || (!isSafari && focusWithRing);
  const isForcedFocusRing = isSafari && focusWithRing;

  const {
    classes: radioClasses,
    styles: { valueLabelStyle }
  } = useComponentTheme<RadioVariantOptions>(RadioRedwoodTheme, {
    disabled: isDisabled ? 'isDisabled' : 'notDisabled',
    focusRing: isForcedFocusRing
      ? 'isForcedFocusRing'
      : isFocusRingSelector
      ? 'isFocusRingSelector'
      : undefined
  });

  const { classes: radioIconClasses } = useComponentTheme(RadioIconRedwoodTheme, {
    disabled: isDisabled ? 'isDisabled' : 'notDisabled',
    selected: isSelected ? 'isSelected' : 'notSelected',
    readonly: isReadonly ? 'isReadonly' : 'notReadonly',
    active: applyActiveStyle ? 'isActive' : 'notActive'
  });

  return (
    <div>
      <label
        class={radioClasses}
        ref={labelRef}
        {...(isReadonly
          ? {
              onFocus: handleFocus,
              onClick: handleLabelClick
            }
          : {})}>
        <HiddenAccessible>
          {isReadonly ? (
            <div
              aria-describedby={helpAvailableId}
              id={id}
              ref={inputRef}
              tabIndex={tabIndex}
              data-checked={isSelected}
              {...focusProps}>
              {accessibilityText}
            </div>
          ) : (
            <input
              type="radio"
              name={name}
              id={id}
              ref={inputRef}
              value={value}
              {...(!isDisabled && {
                onChange: handleChange
              })}
              checked={isSelected}
              disabled={isDisabled}
              aria-describedby={helpAvailableId}
              tabIndex={tabIndex}
              {...focusProps}
            />
          )}
          {hasUserAssistive && (
            <HiddenAccessible id={helpAvailableId} isHidden={true}>
              {helpAvailableString}
            </HiddenAccessible>
          )}
        </HiddenAccessible>
        <span aria-hidden="true" class={radioIconClasses} {...interactionProps}>
          <IconComp size={radioIconVars.iconSize} color="currentColor" />
        </span>
        <span class={valueLabelStyle}>
          <span {...interactionProps}>{children}</span>
          {hasUserAssistive && (
            <IconUserAssistance
              {...assistanceProps}
              isTabbable={isTabbable && isFocus}
              id={uaId as string}
              variant="radio"
            />
          )}
        </span>
      </label>
    </div>
  );
};
